import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import { Grid } from '~/engine/data/common/Grid';
import { trilinearInterpolate } from './trilinearSample';

/**
 * Calculate volume using trilinear interpolation for sub-voxel accuracy.
 * Uses marching cubes-style sampling to estimate partial voxel contributions.
 */
export function calculateVolumeMm3(
    grid: Grid,
    sdf: SignedDistanceField
): number {
    const sdfSize = sdf.size;
    const [dimX, dimY, dimZ] = [sdfSize[0], sdfSize[1], sdfSize[2]];

    if (dimX === 0 || dimY === 0 || dimZ === 0) {
        return 0;
    }

    return _internalCalculateVolumeMm3(
        sdf.pixels,
        grid.resX,
        grid.resY,
        grid.resZ,
        dimX,
        dimY,
        dimZ
    );
}

/**
 * Internal function to calculate volume from SDF pixel data with explicit parameters.
 * Exported for unit testing.
 *
 * @param pixels Array of Uint8Array slices representing SDF data
 * @param resX Voxel resolution in X direction (mm)
 * @param resY Voxel resolution in Y direction (mm)
 * @param resZ Voxel resolution in Z direction (mm)
 * @param dimX Number of voxels in X direction
 * @param dimY Number of voxels in Y direction
 * @param dimZ Number of voxels in Z direction
 * @param options Optional parameters for algorithm tuning
 * @returns Volume in cubic millimeters
 */
export function _internalCalculateVolumeMm3(
    pixels: Uint8Array[],
    resX: number,
    resY: number,
    resZ: number,
    dimX: number,
    dimY: number,
    dimZ: number,
    options: {
        threshold?: number;
        subSamples?: number;
    } = {}
): number {
    if (pixels.length !== dimZ) {
        throw new Error(
            `Pixel array length (${pixels.length}) must match Z dimension (${dimZ})`
        );
    }

    if (resX <= 0 || resY <= 0 || resZ <= 0) {
        throw new Error('All voxel resolutions must be positive');
    }

    if (dimX <= 0 || dimY <= 0 || dimZ <= 0) {
        throw new Error('All dimensions must be positive');
    }

    const threshold = options.threshold ?? 127.5; // SDF inside/outside threshold
    const subSamples = Math.round(options.subSamples ?? 3); // 3x3x3 = 27 sub-samples per voxel

    if (subSamples > 8 || subSamples < 1) {
        throw new Error('subSamples must be between 1 and 8');
    }

    const voxelVolumeMm3 = resX * resY * resZ;
    const subVoxelVolume =
        voxelVolumeMm3 / (subSamples * subSamples * subSamples);
    let totalVolumeMm3 = 0;

    for (let z = 0; z < dimZ - 1; z++) {
        const slice = pixels[z];
        const sliceNext = pixels[z + 1];

        const expectedPixelsPerSlice = dimX * dimY;
        if (slice.length !== expectedPixelsPerSlice) {
            throw new Error(
                `Slice ${z} has ${slice.length} pixels, expected ${expectedPixelsPerSlice} (${dimX}×${dimY})`
            );
        }

        for (let y = 0; y < dimY - 1; y++) {
            for (let x = 0; x < dimX - 1; x++) {
                // Get 8 corner values for trilinear interpolation
                const v000 = getSdfValue(slice, x, y, dimX, dimY);
                const v100 = getSdfValue(slice, x + 1, y, dimX, dimY);
                const v010 = getSdfValue(slice, x, y + 1, dimX, dimY);
                const v110 = getSdfValue(slice, x + 1, y + 1, dimX, dimY);
                const v001 = getSdfValue(sliceNext, x, y, dimX, dimY);
                const v101 = getSdfValue(sliceNext, x + 1, y, dimX, dimY);
                const v011 = getSdfValue(sliceNext, x, y + 1, dimX, dimY);
                const v111 = getSdfValue(sliceNext, x + 1, y + 1, dimX, dimY);

                // Check if voxel is entirely inside or outside
                const allInside =
                    v000 > threshold &&
                    v100 > threshold &&
                    v010 > threshold &&
                    v110 > threshold &&
                    v001 > threshold &&
                    v101 > threshold &&
                    v011 > threshold &&
                    v111 > threshold;

                const allOutside =
                    v000 <= threshold &&
                    v100 <= threshold &&
                    v010 <= threshold &&
                    v110 <= threshold &&
                    v001 <= threshold &&
                    v101 <= threshold &&
                    v011 <= threshold &&
                    v111 <= threshold;

                if (allInside) {
                    // Entire voxel is inside
                    totalVolumeMm3 += voxelVolumeMm3;
                } else if (!allOutside) {
                    // Voxel crosses boundary - use sub-sampling
                    let insideSubSamples = 0;

                    for (let sz = 0; sz < subSamples; sz++) {
                        for (let sy = 0; sy < subSamples; sy++) {
                            for (let sx = 0; sx < subSamples; sx++) {
                                // Sub-sample position within voxel (0 to 1)
                                const fx = (sx + 0.5) / subSamples;
                                const fy = (sy + 0.5) / subSamples;
                                const fz = (sz + 0.5) / subSamples;

                                // Trilinear interpolation
                                const interpolatedValue = trilinearInterpolate(
                                    v000,
                                    v100,
                                    v010,
                                    v110,
                                    v001,
                                    v101,
                                    v011,
                                    v111,
                                    fx,
                                    fy,
                                    fz
                                );

                                if (interpolatedValue > threshold) {
                                    insideSubSamples++;
                                }
                            }
                        }
                    }

                    if (insideSubSamples > 0) {
                        totalVolumeMm3 += insideSubSamples * subVoxelVolume;
                    } else {
                        // Analytical fallback: sub-sampling missed
                        // thin above-threshold regions. Estimate
                        // volume using tetrahedral edge-crossing
                        // interpolation at each above-threshold
                        // corner.
                        totalVolumeMm3 += computeCornerTetrahedraVolume(
                            v000,
                            v100,
                            v010,
                            v110,
                            v001,
                            v101,
                            v011,
                            v111,
                            threshold,
                            voxelVolumeMm3
                        );
                    }
                }
                // If allOutside, add nothing (voxel contributes 0 volume)
            }
        }
    }

    return totalVolumeMm3;
}

/**
 * Safely get SDF value from pixel array with bounds checking.
 * Exported for unit testing.
 */
export function getSdfValue(
    slice: Uint8Array,
    x: number,
    y: number,
    dimX: number,
    dimY: number
): number {
    if (x < 0 || x >= dimX || y < 0 || y >= dimY || !slice) {
        return 0; // Outside bounds = outside structure
    }
    const idx = y * dimX + x;
    return idx < slice.length ? slice[idx] : 0;
}

/**
 * Compute analytical volume contribution for a boundary voxel
 * where the above-threshold region is too thin for sub-sampling
 * to detect. Approximates the enclosed volume by summing a
 * tetrahedron at each above-threshold corner, formed by the
 * three edge-crossing points where the SDF crosses the threshold.
 *
 * Exported for unit testing.
 *
 * @param v000–v111 Corner SDF values of the voxel cell
 * @param threshold SDF inside/outside threshold
 * @param voxelVolume Physical volume of one voxel cell (mm³)
 * @returns Estimated volume (mm³) of the above-threshold region
 */
export function computeCornerTetrahedraVolume(
    v000: number,
    v100: number,
    v010: number,
    v110: number,
    v001: number,
    v101: number,
    v011: number,
    v111: number,
    threshold: number,
    voxelVolume: number
): number {
    // Fast path: all corners above → full voxel
    const vals = [v000, v100, v010, v110, v001, v101, v011, v111];
    if (vals.every((v) => v > threshold)) {
        return voxelVolume;
    }

    // Each corner and its 3 edge-adjacent neighbours.
    // Adjacency follows cube topology:
    //   v000 <-> v100 (x-edge), v010 (y-edge), v001 (z-edge)
    //   etc.
    const corners: [number, number, number, number][] = [
        [v000, v100, v010, v001],
        [v100, v000, v110, v101],
        [v010, v000, v110, v011],
        [v110, v100, v010, v111],
        [v001, v000, v011, v101],
        [v101, v001, v111, v100],
        [v011, v001, v111, v010],
        [v111, v101, v011, v110],
    ];

    let volume = 0;
    for (const [val, a0, a1, a2] of corners) {
        if (val <= threshold) {
            continue;
        }

        // For each edge from this above-threshold corner to an
        // adjacent corner, find the fractional crossing distance.
        // If the adjacent corner is also above threshold the full
        // edge is inside (fraction = 1).
        const edgeFraction = (adj: number): number => {
            if (adj > threshold) {
                return 1;
            }
            // Linear crossing: how far from this corner toward
            // the adjacent corner the iso-surface sits.
            return (val - threshold) / (val - adj);
        };

        const t0 = edgeFraction(a0);
        const t1 = edgeFraction(a1);
        const t2 = edgeFraction(a2);

        // Volume of the tetrahedron at this corner
        volume += (1 / 6) * t0 * t1 * t2 * voxelVolume;
    }

    return volume;
}
